import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { precacheShellAssets } from '@/lib/precache';
import { sessionStore } from '@/lib/storage';
import { BOOTED_KEY } from './reboot';
import { BootScreen } from './BootScreen';
import { WelcomeScreen } from './WelcomeScreen';

type Phase = 'boot' | 'welcome' | 'desktop';

interface BootControllerProps {
  /** Deep link present, or `?noboot=1` — go straight to the desktop. */
  skip: boolean;
  children: ReactNode;
}

/**
 * Gates the desktop behind boot → welcome. Reduced-motion users still see both
 * screens (they are part of the site, not decoration); it is the marquee
 * animation and the cross-fade that BootScreen drops.
 */
export function BootController({ skip, children }: BootControllerProps) {
  const [phase, setPhase] = useState<Phase>(() =>
    skip || sessionStore.get(BOOTED_KEY) === '1' ? 'desktop' : 'boot',
  );

  useEffect(() => {
    if (phase === 'desktop') sessionStore.set(BOOTED_KEY, '1');
  }, [phase]);

  // Boot and Welcome are several seconds of nothing on the wire; spend them
  // pulling the icon set into cache so the desktop and the first folder window
  // do not have to. Idempotent, and it does nothing on a Data Saver connection
  // — see `lib/precache.ts`. Called even when `skip` sent us straight to the
  // desktop: the deep-linked window still wants the rest of the icons.
  useEffect(() => {
    precacheShellAssets();
  }, []);

  // `skip` can flip on after mount — a deep link resolved by the router, say.
  // Honour it rather than stranding the visitor behind the boot screen.
  useEffect(() => {
    if (skip) setPhase('desktop');
  }, [skip]);

  // Stable identities: the child screens key their timers off `onDone`, and a
  // fresh function each render would restart those timers on every render.
  const toWelcome = useCallback(() => {
    setPhase((current) => (current === 'boot' ? 'welcome' : current));
  }, []);

  const toDesktop = useCallback(() => {
    setPhase((current) => (current === 'desktop' ? current : 'desktop'));
  }, []);

  if (phase === 'boot') return <BootScreen onDone={toWelcome} />;
  if (phase === 'welcome') return <WelcomeScreen onDone={toDesktop} />;
  return <>{children}</>;
}
